import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { compose } from 'redux';
import { customization, dialogs, globals, i18next, template, view } from '@yola/ws-sdk';
import segment from 'src/js/modules/analytics/segment';
import {
  SUBMIT_BUTTON_TRIGGER_ID,
  DIALOG_BACKGROUND_TRIGGER_ID,
} from '../../dialogs/constants/common.js';
import constants from '../constants';
import helpers from '../helpers';
import readOnlyModeDecorator from '../helpers/read-only-mode-decorator';
import DesignEffectsDialog from '../components/design-effects-dialog';

const { GLOBAL_HEADER_CODE, GLOBAL_FOOTER_CODE } = constants;

const { applyEffects: applyEffectsHelper, createWebsiteAnimationScript } = helpers;

const {
  constants: { events },
  trackers: { trackEvent },
} = segment;

const getCaptions = () => ({
  breadcrumbs: [i18next.t('Design'), i18next.t('Effects')],
  title: i18next.t('Change website effects'),
  submitButton: i18next.t('Save'),
  cancelButton: i18next.t('Cancel'),
  description: i18next.t(
    'Turn on visual effects to animate elements as they load. Adding effects may slow down website loading'
  ),
  animationTitle: i18next.t('Website animations'),
  animationDescription: i18next.t('Revitalize your site by enabling website animations'),
  animationLabel: i18next.t('Enable website animations'),
});

const DesignEffectsContainer = ({ onDialogMainAction, onDialogCancel, sourceId }) => {
  const effectsVariables = useSelector((state) =>
    template.selectors.getVariableValueByPath(state, customization.constants.EFFECTS_VARIABLE)
  );

  const effects = customization.accessors.getEffectsConfig();

  const [activeEffects, setActiveEffects] = useState(effects);

  const isEffectsChanged = effects.websiteAnimation !== activeEffects.websiteAnimation;
  const captions = getCaptions();

  const applyEffects = (effectsValue) => {
    const liveDocument = view.accessors.getLiveDocument();

    if (effectsValue.websiteAnimation) {
      const headerCodeVariable = effectsVariables.find(
        (item) => item.globalId === GLOBAL_HEADER_CODE && item.querySelector
      );
      const footerCodeVariable = effectsVariables.find(
        (item) => item.globalId === GLOBAL_FOOTER_CODE && item.querySelector
      );
      const headerCode = liveDocument.querySelector(headerCodeVariable.querySelector);
      const footerCode = liveDocument.querySelector(footerCodeVariable.querySelector);
      const headerCodeScript = createWebsiteAnimationScript(GLOBAL_HEADER_CODE);
      const footerCodeScript = createWebsiteAnimationScript(GLOBAL_FOOTER_CODE);

      if (!headerCode && !footerCode) return;

      headerCode.appendChild(headerCodeScript);
      footerCode.appendChild(footerCodeScript);
    } else {
      const body = liveDocument.querySelector('body');
      const scripts = liveDocument.querySelectorAll(
        `[${customization.constants.WEBSITE_ANIMATION_DATA_ATTRIBUTE}]`
      );

      body.removeAttribute(customization.constants.INIT_WEBSITE_ANIMATION_DATA_ATTRIBUTE);
      scripts.forEach((script) => {
        script.remove();
      });
    }
  };

  const onClose = () => {
    dialogs.operations.hide();
  };

  const handleCancel = () => {
    if (effects.websiteAnimation !== activeEffects.websiteAnimation) {
      applyEffects(effects);
    }

    onDialogCancel({
      sourceId,
    });
    onClose();
  };

  const handleSubmit = (event) => {
    if (isEffectsChanged) {
      globals.operations.changeGlobalContent(
        GLOBAL_HEADER_CODE,
        (global) => {
          const transformations = [];
          const applyEffectsCurry = (globalItem) =>
            applyEffectsHelper(globalItem, activeEffects, GLOBAL_HEADER_CODE);
          transformations.push(applyEffectsCurry);

          return compose(...transformations)(global);
        },
        false
      );

      globals.operations.changeGlobalContent(
        GLOBAL_FOOTER_CODE,
        (global) => {
          const applyEffectsCurry = (globalItem) =>
            applyEffectsHelper(globalItem, activeEffects, GLOBAL_FOOTER_CODE);
          return compose(applyEffectsCurry)(global);
        },
        false
      );
    }

    onDialogMainAction({
      triggerId: !event ? DIALOG_BACKGROUND_TRIGGER_ID : SUBMIT_BUTTON_TRIGGER_ID,
      sourceId,
      effectsAdjusted: activeEffects.websiteAnimation
        ? 'website-animation-enabled'
        : 'website-animation-disabled',
    });
    onClose();
  };

  const onChangeEffects = (value) => {
    const newEffects = {
      websiteAnimation: value,
    };

    setActiveEffects(newEffects);
    applyEffects(newEffects);
    trackEvent(events.DESIGN_SETTINGS_EFFECTS_SWITCHER_CLICKED);
  };

  return (
    <DesignEffectsDialog
      captions={captions}
      isWebsiteAnimationChecked={activeEffects.websiteAnimation}
      onWebsiteAnimationChange={readOnlyModeDecorator(onChangeEffects)}
      onSubmit={handleSubmit}
      onCancel={handleCancel}
    />
  );
};

DesignEffectsContainer.propTypes = {
  sourceId: PropTypes.string.isRequired,
  onDialogMainAction: PropTypes.func.isRequired,
  onDialogCancel: PropTypes.func.isRequired,
};

export default DesignEffectsContainer;
